import { useStackModalStore } from '@/common/stores/modalStore';
import { FeedbackRequestWithDetails } from '@/common/types/databaseTypes';
import { useCurrentWorkspace } from '@/features/workspace';
import { useWorkspaceDetails } from '@/features/workspace/services/useWorkspaceQueries';
import { useAuthStore } from '@/stores/authStore';
import {
    ActionIcon,
    Box,
    Button,
    Divider,
    Flex,
    Group,
    Menu,
    Modal,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
} from '@mantine/core';
import {
    IconCalendarEvent,
    IconChevronDown,
    IconChevronUp,
    IconRefresh,
    IconSend,
    IconSettings,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';
import {
    CreateFeedbackParams,
    CreateFeedbackRequestParams,
    feedbackService,
} from '../services/feedbackService';
import {
    useCreateFeedback,
    useCreateFeedbackRequest,
    useUpdateFeedback,
    useUpdateFeedbackRequest,
    FEEDBACK_KEYS,
} from '../services/useFeedbackQueries';
import {
    RequestedFeedbackTodoList,
    FeedbackTodoList,
} from '../utils/FeedbackTodoListRenderers';
import { CoreValueSelector } from './CoreValueSelector';
import { useGroupingStorage } from '@/common/components/Filters/hooks/useGroupingStorage';
import {
    TODO_COMPLETE_VIEW_OPTIONS,
    TodoCompleteViewOption,
} from '@/features/team_member/types/todoViewTypes';
import { useTranslation } from 'react-i18next';
import { hasEditPermission } from '@/common/constants/member';
import { notifications } from '@mantine/notifications';
import { useSearchParams } from 'react-router-dom';
import {
    TAB_SENT_FEEDBACK,
    TAB_SENT_REQUEST,
} from '@/common/constants/constants';
import TodoBadgeList from './TodoBadgeList';
import { queryClient } from '@/queryClient';

export const FeedbackModal = () => {
    const { t } = useTranslation(['common', 'feedback']);
    const { user } = useAuthStore();
    const { openModal, closeModal } = useStackModalStore();

    const { currentWorkspace } = useCurrentWorkspace(user?.id || undefined);

    const { data: workspaceDetails, refetch: refetchWorkspaceDetails } =
        useWorkspaceDetails(currentWorkspace?.id || '');

    const canManageCoreValues = hasEditPermission(
        user?.id || '',
        currentWorkspace?.workspace_member || []
    );
    const coreValues = workspaceDetails?.workspace_core_value || [];

    const {
        isOpen,
        mode,
        data: defaultData,
        feedbackContent,
        selectedUser,
        requestUser,
        positiveCoreValueIds,
        negativeCoreValueIds,
        selectedTodoMapList,
        requestUpdated,
        setFeedbackContent,
        setSelectedUser,
        setPositiveCoreValueIds,
        setNegativeCoreValueIds,
        closeModal: closeFeedbackModal,
        updateRequestData,
    } = useFeedbackModalStore();

    const [searchParams, setSearchParams] = useSearchParams();
    const [isTodosOpen, setIsTodosOpen] = useState(false);

    const createFeedback = useCreateFeedback();
    const createFeedbackRequest = useCreateFeedbackRequest();
    const updateFeedback = useUpdateFeedback();
    const updateFeedbackRequest = useUpdateFeedbackRequest();

    const {
        selectedGrouping: selectedCompleteView,
        setSelectedGrouping: setSelectedCompleteView,
    } = useGroupingStorage<TodoCompleteViewOption>(
        currentWorkspace?.id || '',
        'week', // 기본값: 'week'
        'ws_feedback_complete_view'
    );

    // 피드백인지 요청인지 구분하는 헬퍼 변수
    const is_feedback = useMemo(
        () => mode === 'send' || mode === 'respond' || mode === 'edit-feedback',
        [mode]
    );

    const is_edit = useMemo(
        () => mode === 'edit-feedback' || mode === 'edit-request',
        [mode]
    );

    // 우측에 표시할 사용자를 모드에 따라 결정
    const displayUser = useMemo(
        () =>
            mode === 'respond'
                ? defaultData?.sender // 응답 모드에서는 피드백 요청자 정보 표시
                : is_feedback
                  ? selectedUser // 보내기 모드에서는 선택한 사용자 정보 표시
                  : requestUser, // 요청 모드에서는 나(requestUser)의 정보 표시
        [mode, defaultData, is_feedback, selectedUser, requestUser]
    );

    // 요청에 대한 응답일 경우, 피드백 대상자 선택 컴포넌트 비활성화
    const isUserSelectionDisabled = useMemo(() => mode === 'respond', [mode]);

    // 핵심 가치 설정 모달 열기 함수
    const handleOpenSettingsModal = () => {
        openModal('CORE_VALUE_SETTINGS', {
            workspaceId: currentWorkspace?.id || '',
            coreValues: workspaceDetails?.workspace_core_value || [],
            onSuccess: () => {
                void refetchWorkspaceDetails();
                closeModal(); // 상태 업데이트로 이 모달 위에 피드백 모달이 쌓이게 되어 바로 닫음
            },
        });
    };

    // 모달 닫기
    const handleClose = ({
        shouldConfirmUnsaved = false,
    }: {
        shouldConfirmUnsaved?: boolean;
    } = {}) => {
        // 작성 중인 내용이 있을 경우 모달 띄워주기
        const isUnsaved =
            feedbackContent.trim() !== '' ||
            selectedTodoMapList.length > 0 ||
            positiveCoreValueIds.length > 0 ||
            negativeCoreValueIds.length > 0 ||
            selectedUser;

        if (isUnsaved && shouldConfirmUnsaved) {
            openModal('CONFIRMATION', {
                title: t('feedback:feedbackModal.unsavedContent'),
                message: t('feedback:feedbackModal.unsavedContentDetail'),
                onConfirm: () => {
                    closeModal();
                    closeFeedbackModal();
                },
            });

            return;
        }

        closeFeedbackModal();
    };

    // 업데이트된 요청 데이터 새로고침 핸들러
    const handleRefreshRequestData = () => {
        // 이미 캐시에 있는 데이터에 접근
        const receivedRequests = queryClient.getQueryData<
            FeedbackRequestWithDetails[]
        >([
            ...FEEDBACK_KEYS.receivedRequests(
                user?.id || '',
                currentWorkspace?.id || ''
            ),
        ]);

        // ID로 특정 요청 찾기
        const updatedRequest = receivedRequests?.find(
            request => request.id === defaultData.id
        );

        if (updatedRequest) {
            updateRequestData(updatedRequest);
        }
    };

    // 피드백 생성 로직
    const createFeedbackLogic = async (isCancelledRequest = false) => {
        const receiver =
            mode === 'respond' ? defaultData?.sender : selectedUser;
        if (!receiver) return;

        const notificationId = notifications.show({
            title: t('feedback:action.sendFeedback'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        const newFeedback: CreateFeedbackParams = {
            feedback: {
                content: feedbackContent.trim(),
                receiver_id: receiver.id,
                register_id: user?.id,
                workspace_id: currentWorkspace?.id,
                // 피드백 요청에 응답하는 경우 request_id 설정
                ...(defaultData && !isCancelledRequest
                    ? { request_id: defaultData.id }
                    : {}),
            },
            coreValueIds: {
                positiveIds: positiveCoreValueIds,
                negativeIds: negativeCoreValueIds,
            },
            todoIds: selectedTodoMapList.map(({ id }) => id),
            notiParams: {
                workspaceName: workspaceDetails?.name || '',
                workspaceImageUrl: workspaceDetails?.profile_image_url || '',
            },
        };

        try {
            await createFeedback.mutateAsync(newFeedback);

            notifications.update({
                id: notificationId,
                title: t('feedback:action.sendFeedback'),
                message: t('feedback:sendFeedback.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            console.error('피드백 생성 중 오류 발생', e);

            notifications.update({
                id: notificationId,
                title: t('feedback:action.sendFeedback'),
                message: t('feedback:sendFeedback.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            handleClose();
        }
    };

    // 피드백 보내기 (생성) - 유효성 검사 수행 후 createFeedbackLogic 호출
    const handleCreateFeedback = async () => {
        // 응답일 경우, 요청이 아직 유효한지 체크
        if (mode === 'respond' && defaultData?.id) {
            const request = await feedbackService.getFeedbackRequestById(
                defaultData.id
            );

            // 요청이 취소된 경우
            if (!request || request.enable === false) {
                // 모달 열어 알려주기
                openModal('CONFIRMATION', {
                    title: t('feedback:sendFeedback.requestInvalid'),
                    message: t('feedback:sendFeedback.requestInvalidDetail'),
                    onConfirm: async () => {
                        // 사용자가 확인하면 로직 실행
                        await createFeedbackLogic(true);
                        closeModal();
                    },
                    confirmText: t('button.confirm'),
                    cancelText: t('button.close'),
                });

                return; // 유효하지 않은 요청이면 함수 종료
            }
        }

        // 요청이 유효한 경우 바로 실행
        await createFeedbackLogic();
    };

    // 피드백 요청 보내기 (생성)
    const handleRequestFeedback = async () => {
        if (!selectedUser) return;

        const notificationId = notifications.show({
            title: t('feedback:action.requestFeedback'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        const newFeedbackRequest: CreateFeedbackRequestParams = {
            feedbackRequest: {
                content: feedbackContent.trim(),
                receiver_id: selectedUser.id,
                register_id: user?.id,
                workspace_id: currentWorkspace?.id,
            },
            todoIds: selectedTodoMapList.map(({ id }) => id),
            notiParams: {
                workspaceName: currentWorkspace?.name || '',
                workspaceImageUrl: currentWorkspace?.profile_image_url || '',
            },
        };

        try {
            await createFeedbackRequest.mutateAsync(newFeedbackRequest);

            notifications.update({
                id: notificationId,
                title: t('feedback:action.requestFeedback'),
                message: t('feedback:sendRequest.success'),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (e) {
            console.error('피드백 요청 추가 중 오류 발생', e);
            notifications.update({
                id: notificationId,
                title: t('feedback:action.requestFeedback'),
                message: t('feedback:sendRequest.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            handleClose();
        }
    };

    // 피드백 수정
    const handleUpdateFeedback = async () => {
        if (!selectedUser || !defaultData?.id) return;

        const notificationId = notifications.show({
            title: t('feedback:editFeedback.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        const updatedFeedback = {
            id: defaultData?.id,
            fields: {
                content: feedbackContent.trim(),
                register_id: user?.id,
            },
            todoIds: selectedTodoMapList.map(({ id }) => id),
            coreValueIds: {
                positiveIds: positiveCoreValueIds,
                negativeIds: negativeCoreValueIds,
            },
            notiParams: {
                workspaceName: currentWorkspace?.name || '',
                workspaceImageUrl: currentWorkspace?.profile_image_url || '',
            },
        };

        try {
            await updateFeedback.mutateAsync(updatedFeedback);

            notifications.update({
                id: notificationId,
                title: t('feedback:editFeedback.title'),
                message: t('feedback:sendFeedback.success'),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (e) {
            console.error('피드백 수정 중 오류 발생', e);
            notifications.update({
                id: notificationId,
                title: t('feedback:editFeedback.title'),
                message: t('feedback:sendFeedback.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            handleClose();
        }
    };

    // 피드백 요청 수정
    const handleUpdateFeedbackRequest = async () => {
        if (!selectedUser || !defaultData?.id) return;

        const notificationId = notifications.show({
            title: t('feedback:editRequest.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        const updatedFeedbackRequest = {
            id: defaultData?.id,
            fields: {
                content: feedbackContent.trim(),
                receiver_id: selectedUser.id,
                register_id: user?.id,
                workspace_id: currentWorkspace?.id,
                is_rejected: false,
            },
            todoIds: selectedTodoMapList.map(({ id }) => id),
            notiParams: {
                workspaceName: currentWorkspace?.name || '',
                workspaceImageUrl: currentWorkspace?.profile_image_url || '',
            },
        };

        try {
            await updateFeedbackRequest.mutateAsync(updatedFeedbackRequest);

            notifications.update({
                id: notificationId,
                title: t('feedback:editRequest.title'),
                message: t('feedback:sendRequest.success'),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (e) {
            console.error('피드백 요청 수정 중 오류 발생', e);
            notifications.update({
                id: notificationId,
                title: t('feedback:editRequest.title'),
                message: t('feedback:sendRequest.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            handleClose();
        }
    };

    // 메인 액션 버튼 클릭 핸들러 - 피드백/요청 생성/수정 실행
    const handleButtonClick = async () => {
        // 현재 상황에 맞는 액션 실행
        if (is_edit) {
            if (is_feedback) {
                await handleUpdateFeedback();
            } else {
                await handleUpdateFeedbackRequest();
            }
        } else {
            if (is_feedback) {
                await handleCreateFeedback();
            } else {
                await handleRequestFeedback();
            }
        }

        // 액션 완료 후 탭 전환 로직 (모든 경우에 공통으로 실행)
        const tabNameParam = searchParams.get('tab');

        if (is_feedback && tabNameParam !== TAB_SENT_FEEDBACK) {
            setSearchParams(
                { ...searchParams, tab: TAB_SENT_FEEDBACK },
                { replace: true }
            );
        } else if (!is_feedback && tabNameParam !== TAB_SENT_REQUEST) {
            setSearchParams(
                { ...searchParams, tab: TAB_SENT_REQUEST },
                { replace: true }
            );
        }
    };

    return (
        <Modal
            opened={isOpen}
            onClose={() => handleClose({ shouldConfirmUnsaved: true })}
            withCloseButton={false}
            centered
            radius="lg"
            size="xl"
            styles={{
                content: {
                    height: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
                body: {
                    padding: 0,
                    height: '100%',
                },
            }}
        >
            <Stack gap={0} h="100%">
                <Group
                    gap={0}
                    style={{ flexGrow: 1, overflow: 'auto' }}
                    align="start"
                >
                    <Stack
                        gap={0}
                        flex={1}
                        style={{ overflowY: 'auto', maxHeight: '100%' }}
                    >
                        <Group justify="space-between" pt="20px" px="20px">
                            <Text size="lg" fw={600}>
                                {mode === 'respond'
                                    ? t('feedback:sendFeedback.title', {
                                          name: defaultData?.sender?.name,
                                      })
                                    : is_edit
                                      ? is_feedback
                                          ? t('feedback:action.editFeedback')
                                          : t('feedback:action.editRequest')
                                      : is_feedback
                                        ? t('feedback:action.sendFeedback')
                                        : t('feedback:action.requestFeedback')}
                            </Text>

                            {/* 요청 업데이트 알림 및 새로고침 버튼 */}
                            {mode === 'respond' && requestUpdated && (
                                <Tooltip
                                    label={t(
                                        'feedback:feedbackModal.refreshRequest.tooltip'
                                    )}
                                    position="bottom"
                                    withArrow
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    <Button
                                        size="xs"
                                        variant="light"
                                        color="blue"
                                        radius="md"
                                        leftSection={<IconRefresh size={14} />}
                                        onClick={handleRefreshRequestData}
                                        styles={{
                                            root: {
                                                animation:
                                                    'pulse 2s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%': { opacity: 1 },
                                                    '50%': { opacity: 0.6 },
                                                    '100%': { opacity: 1 },
                                                },
                                            },
                                        }}
                                        w="fit-content"
                                    >
                                        {t(
                                            'feedback:feedbackModal.refreshRequest.button'
                                        )}
                                    </Button>
                                </Tooltip>
                            )}
                        </Group>

                        <Stack
                            p="lg"
                            style={{ overflowY: 'auto', maxHeight: '100%' }}
                        >
                            {mode === 'respond' && (
                                <Stack gap={8}>
                                    <Text size="sm" fw={600}>
                                        {t(
                                            'feedback:feedbackModal.requestTitle'
                                        )}
                                    </Text>
                                    <Text
                                        size="sm"
                                        p={8}
                                        style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {defaultData?.content}
                                    </Text>
                                </Stack>
                            )}

                            {!isUserSelectionDisabled && (
                                <Select
                                    required
                                    placeholder={
                                        is_feedback
                                            ? t(
                                                  'feedback:feedbackModal.selectReceiver'
                                              )
                                            : t(
                                                  'feedback:feedbackModal.selectRequestReceiver'
                                              )
                                    }
                                    value={selectedUser?.id || ''}
                                    data={
                                        currentWorkspace?.workspace_member
                                            .sort((a, b) =>
                                                a.user.name.localeCompare(
                                                    b.user.name
                                                )
                                            )
                                            .filter(
                                                member =>
                                                    member.user.id !== user?.id
                                            )
                                            .map(member => ({
                                                value: member.user.id,
                                                label: member.user.name,
                                            })) || []
                                    }
                                    label={
                                        is_feedback
                                            ? t(
                                                  'feedback:feedbackModal.receiver'
                                              )
                                            : t(
                                                  'feedback:feedbackModal.requestReceiver'
                                              )
                                    }
                                    styles={{
                                        label: {
                                            fontWeight: 600,
                                        },
                                    }}
                                    onChange={value => {
                                        const selectedUser =
                                            currentWorkspace?.workspace_member.find(
                                                member =>
                                                    member.user.id === value
                                            );
                                        setSelectedUser(
                                            selectedUser?.user || null
                                        );
                                    }}
                                />
                            )}

                            {/* 선택된 업무 목록 */}
                            <Group gap="xs" justify="space-between">
                                <Text size="sm" fw={600}>
                                    {t('feedback:feedbackModal.selectedTodo', {
                                        count: selectedTodoMapList.length,
                                    })}
                                </Text>
                                {
                                    <Button
                                        onClick={() =>
                                            setIsTodosOpen(!isTodosOpen)
                                        }
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        leftSection={
                                            isTodosOpen ? (
                                                <IconChevronUp size={12} />
                                            ) : (
                                                <IconChevronDown size={12} />
                                            )
                                        }
                                        styles={{
                                            section: {
                                                marginRight: '4px',
                                            },
                                        }}
                                    >
                                        {isTodosOpen
                                            ? t(
                                                  'feedback:todoBadgeList.collapse'
                                              )
                                            : t(
                                                  'feedback:todoBadgeList.expand'
                                              )}
                                    </Button>
                                }
                            </Group>
                            {isTodosOpen && (
                                <TodoBadgeList
                                    feedbackTodos={selectedTodoMapList.map(
                                        ({ id, title }) => ({
                                            id,
                                            todo: {
                                                id: id,
                                                title: title,
                                                enable: true,
                                            },
                                        })
                                    )}
                                    removable={true}
                                    maxVisible={2}
                                    showAll={true}
                                    marginBottom="0px"
                                />
                            )}

                            {/* feedback input */}
                            <Box
                                pos="relative"
                                flex={1}
                                py="sm"
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                }}
                            >
                                <Textarea
                                    variant="unstyled"
                                    placeholder={
                                        is_feedback
                                            ? t(
                                                  'feedback:feedbackModal.feedbackPlaceHolder'
                                              )
                                            : t(
                                                  'feedback:feedbackModal.requestPlaceHolder'
                                              )
                                    }
                                    value={feedbackContent}
                                    onChange={e =>
                                        setFeedbackContent(e.target.value)
                                    }
                                    minRows={5}
                                    maxRows={10}
                                    autosize
                                    styles={{
                                        // 스크롤바 유의해서 여백 스타일링
                                        input: {
                                            padding: '0 16px',
                                            scrollbarGutter: 'stable',
                                            scrollbarWidth: 'thin',
                                            '&::WebkitScrollbar': {
                                                width: '6px',
                                            },
                                            '&::WebkitScrollbarTrack': {
                                                background: 'transparent',
                                                marginRight: '8px',
                                            },
                                            '&::WebkitScrollbarThumb': {
                                                background: '#e0e0e0',
                                                borderRadius: '3px',
                                            },
                                        },
                                    }}
                                />

                                {/* buttons for ai */}
                                {/* <Group justify="end" gap={8} px="md">
                                    <Button
                                        variant="default"
                                        color="gray"
                                        size="xs"
                                        radius={8}
                                        onClick={() => {
                                            console.log('feedbackScoring');
                                        }}
                                    >
                                        {t(
                                            'feedback:feedbackModal.feedbackScoring'
                                        )}
                                    </Button>
                                    <Button
                                        variant="filled"
                                        size="xs"
                                        radius={8}
                                        onClick={() => {
                                            console.log('aiOptimization');
                                        }}
                                    >
                                        {t(
                                            'feedback:feedbackModal.aiOptimization'
                                        )}
                                    </Button>
                                </Group> */}
                            </Box>

                            {/* core value */}
                            {is_feedback && (
                                <>
                                    <Group gap="xs">
                                        <Text size="sm" fw={600}>
                                            {t(
                                                'feedback:feedbackModal.selectCoreValue'
                                            )}
                                        </Text>
                                        {canManageCoreValues && (
                                            <ActionIcon
                                                variant="subtle"
                                                color="gray"
                                                size="xs"
                                                c="gray"
                                                onClick={
                                                    handleOpenSettingsModal
                                                }
                                            >
                                                <IconSettings size={12} />
                                            </ActionIcon>
                                        )}
                                    </Group>
                                    {/* 강점 핵심가치 선택 */}
                                    <CoreValueSelector
                                        title={t(
                                            'feedback:feedbackModal.positivePlaceHolder'
                                        )}
                                        isPositive={true}
                                        selectedValues={positiveCoreValueIds}
                                        disabledValues={negativeCoreValueIds}
                                        coreValues={coreValues}
                                        onValueChange={setPositiveCoreValueIds}
                                    />

                                    {/* 개선점 핵심가치 선택 */}
                                    <CoreValueSelector
                                        title={t(
                                            'feedback:feedbackModal.negativePlaceHolder'
                                        )}
                                        isPositive={false}
                                        selectedValues={negativeCoreValueIds}
                                        disabledValues={positiveCoreValueIds}
                                        coreValues={coreValues}
                                        onValueChange={setNegativeCoreValueIds}
                                    />
                                </>
                            )}
                        </Stack>
                    </Stack>
                    <Divider orientation="vertical" />
                    {displayUser ? (
                        <Stack
                            flex={1}
                            gap="xs"
                            style={{ overflowY: 'auto', maxHeight: '100%' }}
                        >
                            <Stack
                                gap={4}
                                style={{ padding: '20px 20px 0 20px' }}
                            >
                                <Text size="lg" fw={600}>
                                    {t('feedback:feedbackModal.todoList', {
                                        name: displayUser.name,
                                    })}
                                </Text>
                                <Group justify="space-between">
                                    <Text size="xs" c="dimmed">
                                        {mode === 'respond'
                                            ? t(
                                                  'feedback:feedbackModal.todoListDescription.respond'
                                              )
                                            : is_feedback
                                              ? t(
                                                    'feedback:feedbackModal.todoListDescription.feedback'
                                                )
                                              : t(
                                                    'feedback:feedbackModal.todoListDescription.request'
                                                )}
                                    </Text>

                                    {/* 요청에 대한 응답이 아닐 경우 투두 필터링 메뉴 추가 */}
                                    {mode !== 'respond' && (
                                        <Menu>
                                            <Menu.Target>
                                                <Button
                                                    variant="default"
                                                    color="gray"
                                                    size="xs"
                                                    radius="md"
                                                    styles={{
                                                        inner: {
                                                            gap: 4,
                                                        },
                                                        section: {
                                                            margin: 0,
                                                        },
                                                    }}
                                                    leftSection={
                                                        <IconCalendarEvent
                                                            size={14}
                                                        />
                                                    }
                                                >
                                                    <Text size="xs">
                                                        {
                                                            TODO_COMPLETE_VIEW_OPTIONS.find(
                                                                option =>
                                                                    option.value ===
                                                                    selectedCompleteView
                                                            )?.label
                                                        }
                                                    </Text>
                                                </Button>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                {TODO_COMPLETE_VIEW_OPTIONS.map(
                                                    option => (
                                                        <Menu.Item
                                                            key={option.value}
                                                            onClick={() =>
                                                                setSelectedCompleteView(
                                                                    option.value
                                                                )
                                                            }
                                                            fz="xs"
                                                            bg={
                                                                selectedCompleteView ===
                                                                option.value
                                                                    ? 'var(--mantine-color-gray-1)'
                                                                    : undefined
                                                            }
                                                        >
                                                            {option.label}
                                                        </Menu.Item>
                                                    )
                                                )}
                                            </Menu.Dropdown>
                                        </Menu>
                                    )}
                                </Group>
                            </Stack>
                            <Stack
                                gap={0}
                                px="lg"
                                style={{
                                    overflowY: 'auto',
                                    maxHeight: '100%',
                                }}
                            >
                                {mode === 'respond' ? (
                                    // 피드백 요청에 포함된 todo 항목 표시
                                    <RequestedFeedbackTodoList
                                        todoDatas={
                                            (
                                                defaultData as FeedbackRequestWithDetails
                                            )?.feedback_request_todos || []
                                        }
                                    />
                                ) : (
                                    <FeedbackTodoList
                                        userId={displayUser.id}
                                        workspaceTeamIds={
                                            currentWorkspace?.teams?.map(
                                                team => team.id
                                            ) || []
                                        }
                                        workspaceId={currentWorkspace?.id}
                                        selectedCompleteView={
                                            selectedCompleteView
                                        }
                                    />
                                )}
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack
                            flex={1}
                            p="lg"
                            justify="center"
                            align="center"
                            h="100%"
                        >
                            <Text c="dimmed">
                                {is_feedback
                                    ? t(
                                          'feedback:feedbackModal.receiverPlaceHolder'
                                      )
                                    : t(
                                          'feedback:feedbackModal.requestReceiverPlaceHolder'
                                      )}
                            </Text>
                        </Stack>
                    )}
                </Group>
                <Divider />
                <Flex justify="end" px="lg" py="sm">
                    <Group gap="xs">
                        <Button
                            variant="outline"
                            onClick={() => handleClose()}
                            color="gray"
                            radius="md"
                        >
                            {t('common:button.cancel')}
                        </Button>
                        <Button
                            leftSection={
                                is_feedback ? <IconSend size={16} /> : null
                            }
                            disabled={
                                !displayUser || feedbackContent.trim() === ''
                            }
                            radius="md"
                            onClick={() => void handleButtonClick()}
                        >
                            {is_edit
                                ? t('feedback:action.doneEdit')
                                : is_feedback
                                  ? t('feedback:action.sendFeedback')
                                  : t('feedback:action.requestFeedback')}
                        </Button>
                    </Group>
                </Flex>
            </Stack>
        </Modal>
    );
};
